// ===================================================
// GAM Command Center — internal chat (מצע הצ׳אט, שלב 1)
// Reads/writes chat_conversations + chat_messages under the two-layer RLS from
// migration 20260721011105: (1) you must be a member, (2) audience='internal'
// additionally requires user_profiles.role IN ('internal','admin').
//
// Agent messages are NOT written from here — `chat_msg_insert` only accepts
// sender_type='user' with sender_id = auth.uid(). The system agent writes through
// the `chat-post-message` Edge Function on the service role.
//
// Spec: docs/specs/chat-substrate-stage1-internal-alerts.md
// ===================================================
import { supabase } from "@/lib/supabaseClient";
import type { RealtimeChannel } from "@supabase/supabase-js";

export interface ChatConversation {
  id: string;
  kind: "channel" | "dm";
  audience: "internal" | "customer" | "lead";
  title: string | null;
  created_at: string;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_type: "user" | "agent" | "guest";
  sender_id: string | null;
  body: string;
  created_at: string;
  edited_at: string | null;
  deleted_at: string | null;
}

export interface ChatAgent {
  id: string;
  handle: string;
  display_name: string;
}

/** The alert channel is addressed by title everywhere — the VPS script holds no UUIDs. */
export const ALERTS_CHANNEL_TITLE = "התראות מערכת";

export async function fetchConversations(): Promise<ChatConversation[]> {
  const { data, error } = await supabase
    .from("chat_conversations")
    .select("id, kind, audience, title, created_at")
    .is("archived_at", null)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []) as ChatConversation[];
}

export async function fetchMessages(conversationId: string, limit = 200): Promise<ChatMessage[]> {
  const { data, error } = await supabase
    .from("chat_messages")
    .select("id, conversation_id, sender_type, sender_id, body, created_at, edited_at, deleted_at")
    .eq("conversation_id", conversationId)
    .is("deleted_at", null)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  // Newest-first from the DB (so the limit keeps the RECENT tail, not the oldest),
  // flipped here because the transcript renders oldest → newest.
  return ((data ?? []) as ChatMessage[]).reverse();
}

export async function fetchAgents(): Promise<ChatAgent[]> {
  const { data, error } = await supabase.from("chat_agents").select("id, handle, display_name");
  if (error) throw error;
  return (data ?? []) as ChatAgent[];
}

export async function sendMessage(conversationId: string, body: string): Promise<ChatMessage> {
  const { data: auth } = await supabase.auth.getUser();
  const uid = auth.user?.id;
  if (!uid) throw new Error("not_authenticated");

  const { data, error } = await supabase
    .from("chat_messages")
    .insert({
      conversation_id: conversationId,
      sender_type: "user",
      sender_id: uid,
      body: body.trim(),
    })
    .select("id, conversation_id, sender_type, sender_id, body, created_at, edited_at, deleted_at")
    .single();
  if (error) throw error;
  return data as ChatMessage;
}

/**
 * Live messages for one conversation. RLS is still applied per row on the wire —
 * a non-member simply never receives the payload.
 */
export function subscribeToMessages(
  conversationId: string,
  onInsert: (msg: ChatMessage) => void
): RealtimeChannel {
  return supabase
    .channel(`chat-messages-${conversationId}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "chat_messages",
        filter: `conversation_id=eq.${conversationId}`,
      },
      (payload) => onInsert(payload.new as ChatMessage)
    )
    .subscribe();
}

/**
 * Live messages across EVERY conversation the viewer can see — this is what drives the
 * sidebar unread badge and the notification, so it must not be scoped to the open channel.
 */
export function subscribeToAllMessages(onInsert: (msg: ChatMessage) => void): RealtimeChannel {
  return supabase
    .channel("chat-messages-all")
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "chat_messages" },
      (payload) => onInsert(payload.new as ChatMessage)
    )
    .subscribe();
}

export function unsubscribe(channel: RealtimeChannel): void {
  supabase.removeChannel(channel);
}
